/**
 * LA NUIT, TOUTES BILLETTERIES — la logique d'affichage, sans React.
 *
 * Le 15/10/2026, la carte du direct disait 421 billets scannés (L'ÉTUDIANTE,
 * Shotgun) ; la porte en avait vu 636 — MEDECINE, une soirée d'association
 * vendue sur Pretix, en apportait 215. Le serveur compose la nuit
 * (`VueNuit`) ; ce fichier décide seulement de ce que chaque écran en montre,
 * selon Réglages Soirées → Billetteries.
 *
 * Toujours des ENTRÉES — des billets scannés —, jamais des « personnes
 * présentes » : aucune sortie n'est scannée.
 */

export interface LigneNuit {
  cle: string
  libelle: string
  /** Nombre d'événements regroupés (1 = une soirée seule). */
  nombre: number
  source: string
  /** « Shotgun », « Pretix ». */
  billetterie: string
  associations: boolean
  soirees: Array<{ id: string; name: string }>
  scanned: number
  valid: number
  en_cours: boolean
  /** Nombre FIGÉ : la billetterie n'est plus lue (absent d'un serveur d'avant le 16/10/2026). */
  perime?: boolean
  derniere_lecture?: string | null
}

export interface VueNuit {
  nuit: string
  en_cours: boolean
  scanned: number
  valid: number
  lignes: LigneNuit[]
  /** « 636 entrées cette nuit : L'ÉTUDIANTE 421 (Shotgun) · MEDECINE 215 (Pretix) ». */
  phrase: string
  /** Vrai quand la nuit compte une soirée d'association (une nuit Shotgun seule reste comme avant). */
  multiple: boolean
  /** Au moins une ligne est figée : le total l'est aussi. */
  perime?: boolean
  libelle_associations: string
}

/** Ce que le serveur dit des réglages d'affichage (jamais plus). */
export interface AffichageBilletteries {
  live_count: 'NIGHT' | 'SOIREE'
  show_dashboard: boolean
  show_cockpit: boolean
  pretix_label: string
  /** Les soirées d'associations comptent-elles dans les prévisions et comparaisons ? */
  include_in_analytics?: boolean
  /** Venue par formule et par association (19/10/2026). */
  show_no_show?: boolean
  /** « Formules pour le bar » (19/10/2026). */
  show_bar_formules?: boolean
  /** Sur une soirée association, la tuile Billetterie : toute la nuit, le club seul, ou masquée (20/10/2026). */
  association_tile?: 'NIGHT' | 'CLUB' | 'HIDDEN'
}

/** Un serveur d'avant les billetteries : le comportement de toujours. */
export const AFFICHAGE_DEFAUT: AffichageBilletteries = {
  live_count: 'NIGHT', show_dashboard: true, show_cockpit: true, pretix_label: 'Entrées BDE (Pretix)',
}

/** « INTERSANTE (×9) » pour un groupe d'événements, le nom seul sinon. */
export function libelleLigne(l: Pick<LigneNuit, 'libelle' | 'nombre'>): string {
  return l.nombre > 1 ? `${l.libelle} (×${l.nombre})` : l.libelle
}

const nombre = (n: number) => n.toLocaleString('fr-FR')

/** « 636 entrées cette nuit » — « cette nuit-là » pour une nuit passée. */
export function enteteNuit(v: Pick<VueNuit, 'scanned' | 'en_cours'>): string {
  return `${nombre(v.scanned)} entrée${v.scanned > 1 ? 's' : ''} ${v.en_cours ? 'cette nuit' : 'cette nuit-là'}`
}

export type LieuNuit = 'soirees' | 'dashboard' | 'cockpit'

/**
 * La nuit mérite-t-elle d'être montrée ici ? Seulement quand elle dit autre
 * chose que la soirée seule, et là où le réglage l'autorise.
 */
export function afficherLaNuit(nuit: VueNuit | null | undefined, a: AffichageBilletteries | null | undefined, lieu: LieuNuit): nuit is VueNuit {
  if (!nuit || !nuit.multiple || nuit.lignes.length === 0) return false
  const r = a ?? AFFICHAGE_DEFAUT
  if (lieu === 'dashboard') return r.show_dashboard
  if (lieu === 'cockpit') return r.show_cockpit
  return true
}

/**
 * LE GRAND NOMBRE DU DIRECT. Réglage « total de la nuit » (défaut) : la nuit
 * entière, toutes billetteries, dès qu'elle compte plus que la soirée.
 * Réglage « la soirée seule » : le nombre de la soirée, la nuit à côté.
 */
export function grandNombre(
  soireeScanned: number | null, nuit: VueNuit | null | undefined, a: AffichageBilletteries | null | undefined, lieu: LieuNuit,
): { valeur: number | null; libelle: string; estLaNuit: boolean } {
  const r = a ?? AFFICHAGE_DEFAUT
  if (r.live_count === 'NIGHT' && afficherLaNuit(nuit, r, lieu) && nuit.en_cours) {
    return { valeur: nuit.scanned, libelle: 'Entrées cette nuit', estLaNuit: true }
  }
  return { valeur: soireeScanned, libelle: 'Billets scannés', estLaNuit: false }
}

/** « Nuit du jeudi 15 octobre » — la nuit commerciale, en clair. */
export function nuitFr(nuit: string): string {
  const d = new Date(`${nuit}T12:00:00Z`)
  if (Number.isNaN(d.getTime())) return nuit
  return `Nuit du ${d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Europe/Paris' })}`
}

/** Les billetteries d'associations — la même liste que le serveur (`SOURCES_ASSOCIATIONS`). */
export const SOURCES_ASSOCIATIONS: readonly string[] = ['pretix']

/** « dernières données fiables il y a 34 min » — la même phrase que le serveur. */
export function depuisDerniereLecture(iso: string | null | undefined, nowMs = Date.now()): string {
  if (!iso) return 'aucune lecture réussie'
  const min = Math.max(0, Math.round((nowMs - Date.parse(iso)) / 60_000))
  if (min < 1) return 'dernières données fiables il y a moins d’une minute'
  if (min < 120) return `dernières données fiables il y a ${min} min`
  return `dernières données fiables il y a ${Math.round(min / 60)} h`
}

/**
 * L'AVERTISSEMENT D'UNE NUIT FIGÉE — `null` quand tout est lu.
 *
 * Le 15/10, une panne de Pretix à 1 h aurait laissé « MEDECINE 120 » dans le
 * total, sous la fraîcheur de Shotgun (« il y a 20 s ») : un nombre figé
 * présenté comme vivant. La conséquence d'abord : « Le total est incomplet ».
 */
export function avertissementNuit(nuit: Pick<VueNuit, 'lignes'> | null | undefined, nowMs = Date.now()): string | null {
  const figees = (nuit?.lignes ?? []).filter((l) => l.perime)
  if (figees.length === 0) return null
  const parBilletterie = new Map<string, string | null>()
  for (const l of figees) {
    const avant = parBilletterie.get(l.billetterie)
    const d = l.derniere_lecture ?? null
    if (!parBilletterie.has(l.billetterie) || (d !== null && (avant === null || avant === undefined || d < avant))) parBilletterie.set(l.billetterie, d)
  }
  const detail = [...parBilletterie].map(([b, d]) => `${b} : ${depuisDerniereLecture(d, nowMs)}`).join(' · ')
  return `Total peut-être incomplet — ${detail}.`
}

/**
 * LES TUILES QU'UNE SOIRÉE D'ASSOCIATION N'A PAS. Sa billetterie n'entre pas
 * en Comptabilité (la tuile « valeur faciale » montrait celle de la soirée du
 * club) ; hors analyses (réglage par défaut), elle n'a ni prévision ni
 * comparaison — bâties sur les soirées du club, elles ne voudraient rien dire.
 */
export function tuilesAbsentesPourSource(source: string | null | undefined, a: Pick<AffichageBilletteries, 'include_in_analytics'> | null | undefined): string[] {
  if (!source || !SOURCES_ASSOCIATIONS.includes(source)) return []
  const absentes = ['finance']
  if (!(a?.include_in_analytics ?? false)) absentes.push('comparison', 'forecast-current', 'forecast-vs-actual', 'forecast-history')
  return absentes
}

/* ═══════════ LA TUILE BILLETTERIE D'UNE SOIRÉE ASSOCIATION (20/10/2026) ═══════════ */

/**
 * Le 09/09 (INTERSANTE), la tuile Billetterie de la fiche — et la carte
 * Sécurité, que lit la porte — affichaient un grand 0 « Billets scannés » :
 * la billetterie du club, Shotgun, n'avait rien vendu. 894 personnes étaient
 * entrées par les 8 événements Pretix. Décision de l'exploitant : sur une
 * soirée association, la tuile compte TOUTE LA NUIT, toutes billetteries,
 * avec le détail dessous (réglage : le club seul, ou masquer).
 *
 * Le nombre et son détail viennent du même objet (`VueNuit`, tous les
 * événements de la nuit) : ils s'additionnent toujours. Une nuit ordinaire
 * (15/10 : 711 Shotgun, 249 Pretix) ne change pas : pas d'étiquette, pas de
 * bascule.
 */
export interface TuileBilletterie {
  /** NUIT : toute la nuit ; SOIREE : la billetterie de la soirée (comme toujours) ; MASQUEE : rien (réglage). */
  mode: 'NUIT' | 'SOIREE' | 'MASQUEE'
  valeur: number | null
  libelle: string
  vendus: number | null
  /** Taux de scan en %, une décimale ; `null` sans billet vendu. */
  taux: number | null
  /** La nuit, en mode NUIT (son détail par billetterie s'affiche dessous). */
  nuit: VueNuit | null
}

const arrondi1 = (x: number) => Math.round(x * 10) / 10

/** Ce que montre la tuile Billetterie (et la carte Sécurité) — PURE. `association` : l'étiquette « Soirée association » du serveur. */
export function tuileBilletterie(
  soiree: { scanned: number | null; valid: number | null; taux: number | null },
  nuit: VueNuit | null | undefined,
  association: object | null | undefined,
  a: Pick<AffichageBilletteries, 'association_tile'> | null | undefined,
): TuileBilletterie {
  const reglage = a?.association_tile ?? 'NIGHT'
  if (association) {
    if (reglage === 'HIDDEN') return { mode: 'MASQUEE', valeur: null, libelle: '', vendus: null, taux: null, nuit: null }
    if (reglage === 'NIGHT' && nuit && nuit.lignes.length > 0) {
      return {
        mode: 'NUIT', valeur: nuit.scanned, vendus: nuit.valid, nuit,
        taux: nuit.valid > 0 ? arrondi1((nuit.scanned * 100) / nuit.valid) : null,
        // Les mêmes mots que l'en-tête de la nuit : « cette nuit » en direct, « cette nuit-là » ensuite.
        libelle: nuit.en_cours ? 'Entrées cette nuit' : 'Entrées cette nuit-là',
      }
    }
  }
  return { mode: 'SOIREE', valeur: soiree.scanned, vendus: soiree.valid, taux: soiree.taux, libelle: 'Billets scannés', nuit: null }
}

/**
 * La tuile de la fiche, lue dans la réponse du détail (`/api/v1/soirees/:id`)
 * — PURE. Elle compte `night_soiree` : la soirée et ses événements
 * rattachés, le même périmètre que ses tiroirs (`scope=nuit`) — jamais un
 * événement « à rattacher » ou gardé séparé la même nuit (revue du 20/10).
 */
export function tuileDeLaFiche(d: {
  soiree: { stats: { tickets_scanned: number; tickets_valid: number } | null; scan_rate: number | null }
  night_soiree?: VueNuit | null
  rattachement?: { soiree_association?: object | null } | null
  billetteries?: Pick<AffichageBilletteries, 'association_tile'>
} | null | undefined): TuileBilletterie {
  return tuileBilletterie(
    { scanned: d?.soiree.stats?.tickets_scanned ?? null, valid: d?.soiree.stats?.tickets_valid ?? null, taux: d?.soiree.scan_rate ?? null },
    d?.night_soiree, d?.rattachement?.soiree_association ?? null, d?.billetteries,
  )
}

/**
 * Ce que la nuit compte EN DEHORS de la soirée et de ses rattachés — un
 * événement « à rattacher » ou gardé séparé la même nuit. La tuile ne
 * l'additionne pas (ses tiroirs non plus) ; elle le dit, pour que rien ne
 * disparaisse sans un mot. `null` quand il n'y a rien à dire — PURE.
 */
export function horsDeLaSoiree(night: Pick<VueNuit, 'scanned'> | null | undefined, nuitSoiree: Pick<VueNuit, 'scanned'> | null | undefined): string | null {
  if (!night || !nuitSoiree) return null
  const n = night.scanned - nuitSoiree.scanned
  if (n <= 0) return null
  return `Hors de ce total : ${n.toLocaleString('fr-FR')} entrée${n > 1 ? 's' : ''} d’événements de la même nuit qui ne sont pas rattachés à cette soirée.`
}

/** « 91,3 % » ; « — » sans taux. */
export function tauxFr(taux: number | null): string {
  return taux === null ? '—' : `${taux.toLocaleString('fr-FR')} %`
}

/** Les ventes de la nuit par billetterie (tiroir « Ventes » d'une soirée association) — PURE. */
export function ventesParBilletterie(nuit: Pick<VueNuit, 'lignes'>): Array<{ billetterie: string; valid: number; scanned: number }> {
  const out = new Map<string, { billetterie: string; valid: number; scanned: number }>()
  for (const l of nuit.lignes) {
    const x = out.get(l.billetterie) ?? { billetterie: l.billetterie, valid: 0, scanned: 0 }
    x.valid += l.valid; x.scanned += l.scanned
    out.set(l.billetterie, x)
  }
  return [...out.values()]
}

/** La fraîcheur de la billetterie de la soirée (`ticketingFreshness`), telle que la fiche la reçoit. */
export interface FraicheurClub {
  state: string
  seconds_since_success: number | null
  stale: boolean
  configured: boolean
}

function ageCourt(s: number): string {
  if (s < 10) return 'à l’instant'
  if (s < 120) return `il y a ${s} s`
  if (s < 7200) return `il y a ${Math.round(s / 60)} min`
  return `il y a ${Math.round(s / 3600)} h`
}

/**
 * « Shotgun : il y a 20 s · Pretix : il y a 25 s » — l'âge de CHAQUE
 * billetterie de la nuit. La tuile comptée sur toute la nuit n'affiche plus
 * la seule fraîcheur de Shotgun sous un nombre qui vient de Pretix. `perimee`
 * : au moins une billetterie ne donne plus de données fiables. PURE.
 *
 * UNE SEULE SOURCE PAR BILLETTERIE (revue du 20/10/2026) — celle que le bloc
 * dessous lit déjà, pour que la ligne ne le contredise jamais :
 *   · une billetterie d'associations : l'état de lecture DE CETTE NUIT
 *     (`LigneNuit.perime` / `derniere_lecture`). Un tour suspendu par le mode
 *     sûr compte comme « réussi » dans l'âge de la source : la ligne disait
 *     « Pretix : il y a 20 s » au-dessus de « figé ». Une nuit passée lue
 *     après sa fin est complète — jamais « jamais lue » ni un âge en ambre
 *     parce que Pretix est en panne ou éteint AUJOURD'HUI ;
 *   · la billetterie de la soirée : sa fraîcheur, comme la ligne de toujours
 *     (`FreshnessLine`).
 */
export function fraicheursDeLaNuit(
  nuit: Pick<VueNuit, 'lignes' | 'en_cours'> | null | undefined, club: FraicheurClub | null | undefined, nowMs = Date.now(),
): { texte: string; perimee: boolean } | null {
  if (!nuit || nuit.lignes.length === 0) return null
  const sources = [...new Set(nuit.lignes.map((l) => l.source))]
  const morceaux = sources.map((src) => {
    const lignes = nuit.lignes.filter((l) => l.source === src)
    const nom = lignes[0]!.billetterie
    if (lignes[0]!.associations) {
      const figees = lignes.filter((l) => l.perime)
      if (figees.length > 0) {
        // La plus ancienne lecture : celle qui borne le total, la même que l'avertissement.
        const d = figees.map((l) => l.derniere_lecture ?? null).reduce<string | null>((a, x) => (a === null || (x !== null && x < a) ? x : a), null)
        return { t: `${nom} : ${depuisDerniereLecture(d, nowMs)}`, p: true }
      }
      if (!nuit.en_cours) return { t: `${nom} : complet, relu après la nuit`, p: false }
      const d = lignes[0]!.derniere_lecture ?? null
      return d === null
        ? { t: `${nom} : première lecture en attente`, p: false }
        : { t: `${nom} : ${ageCourt(Math.max(0, Math.round((nowMs - Date.parse(d)) / 1000)))}`, p: false }
    }
    if (!club) return null
    if (!club.configured) return { t: `${nom} : non configurée`, p: true }
    if (club.seconds_since_success === null) return { t: `${nom} : première synchronisation en attente`, p: false }
    return { t: `${nom} : ${club.stale ? 'dernières données fiables ' : ''}${ageCourt(club.seconds_since_success)}`, p: club.stale }
  }).filter((m): m is { t: string; p: boolean } => m !== null)
  if (morceaux.length === 0) return null
  return { texte: morceaux.map((m) => m.t).join(' · '), perimee: morceaux.some((m) => m.p) }
}
